import React, { useState, useEffect } from 'react';
import { Mail, Users, UserCheck, UserX, Trash2, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const NewsletterPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await API.get('/admin/newsletter');
      setSubscribers(response.data.subscribers);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the newsletter?`)) {
      return;
    }
    
    setDeleting(id);
    try {
      await API.delete(`/admin/newsletter/${id}`);
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    } finally {
      setDeleting(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Status', 'Subscribed Date'];
    const rows = subscribers.map(s => [
      s.email,
      s.subscribed ? 'Active' : 'Unsubscribed',
      new Date(s.created_at).toLocaleDateString()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export downloaded');
  };

  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C5530]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="newsletter-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-['Playfair_Display'] font-medium text-[#1A1A1A]">
            Newsletter Subscribers
          </h1>
          <p className="text-[#57534E] mt-1">
            Manage your email newsletter subscribers
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C5530] text-white rounded hover:bg-[#1E3A21] transition-colors"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg border border-[#E7E5E4]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#2C5530]/10 rounded-lg">
              <Users className="w-5 h-5 text-[#2C5530]" />
            </div>
            <div>
              <p className="text-sm text-[#57534E]">Total Subscribers</p>
              <p className="text-2xl font-semibold text-[#1A1A1A]">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-[#E7E5E4]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-[#57534E]">Active</p>
              <p className="text-2xl font-semibold text-[#1A1A1A]">{stats.active}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-[#E7E5E4]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-[#57534E]">Unsubscribed</p>
              <p className="text-2xl font-semibold text-[#1A1A1A]">{stats.unsubscribed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#57534E]" />
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-[#E7E5E4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5530]/20 focus:border-[#2C5530]"
          data-testid="search-input"
        />
      </div>

      {/* Subscribers Table */}
      <div className="bg-white rounded-lg border border-[#E7E5E4] overflow-hidden">
        {filteredSubscribers.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#57534E]">
              {searchTerm ? 'No subscribers found matching your search' : 'No newsletter subscribers yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#57534E] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#57534E] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#57534E] uppercase tracking-wider">
                    Subscribed Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#57534E] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E7E5E4]">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-[#FAFAF9]" data-testid={`subscriber-row-${subscriber.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#2C5530]/10 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-[#2C5530]" />
                        </div>
                        <span className="text-[#1A1A1A]">{subscriber.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {subscriber.subscribed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          <UserCheck className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                          <UserX className="w-3 h-3" />
                          Unsubscribed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#57534E]">
                      {formatDate(subscriber.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(subscriber.id, subscriber.email)}
                        disabled={deleting === subscriber.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Remove subscriber"
                        data-testid={`delete-btn-${subscriber.id}`}
                      >
                        {deleting === subscriber.id ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Note */}
      <p className="text-sm text-[#57534E] text-center">
        Subscribers are collected from the newsletter signup form on the website.
      </p>
    </div>
  );
};

export default NewsletterPage;
